import { codeFromCodeableConcept } from '@seen/core/codeableConcept'
import {
  BLOOD_PRESSURE_CODING,
  BODY_HEIGHT_CODING,
  BODY_TEMPERATURE_CODING,
  BODY_WEIGHT_CODING,
  HEART_RATE_CODING,
  OXYGEN_SATURATION_CODING,
  PAIN_SEVERITY_CODING,
  RESPIRATORY_RATE_CODING,
} from '@seen/core/models/loinc'
import type { AnyFunction } from '@seen/core/utils'
import type { Extensions } from '@tiptap/react'
import type { DebouncedFunc } from 'lodash-es'
import { type JSX, useEffect } from 'react'
import { match } from 'ts-pattern'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'

// A vital's value control: one line of text, its number picked out as typed.
// Rendered inside the node's ObservationNodeFrame.
export function ObservationVitalInput({
  code,
  value,
  debouncedOnChange,
  additionalExtensions,
  autofocus,
  onAutofocused,
  disabled,
  noValue,
}: {
  code: string
  value: string
  debouncedOnChange: DebouncedFunc<AnyFunction>
  additionalExtensions: Extensions
  autofocus?: boolean
  onAutofocused?: () => void
  disabled?: boolean
  // Locked before anything was entered: read as such, not as an empty field.
  noValue?: boolean
}): JSX.Element {
  useEffect(() => {
    if (autofocus) {
      onAutofocused?.()
    }
  }, [autofocus, onAutofocused])

  return noValue ? (
    <span className="text-foreground-secondary">no value</span>
  ) : (
    <RichTextEditor
      variant="unstyled"
      placeholder={getPlaceholder(code)}
      onChange={debouncedOnChange}
      className="[&_.is-empty]:before:underline"
      value={value}
      additionalExtensions={additionalExtensions}
      onBlur={debouncedOnChange.flush}
      autofocus={autofocus ? 'end' : false}
      disabled={disabled}
    />
  )
}

function getPlaceholder(code: string): string {
  return match<string | undefined>(code)
    .with(codeFromCodeableConcept(BODY_TEMPERATURE_CODING), () => '°F   ')
    .with(codeFromCodeableConcept(BODY_WEIGHT_CODING), () => 'lbs  ')
    .with(codeFromCodeableConcept(BODY_HEIGHT_CODING), () => 'inches')
    .with(codeFromCodeableConcept(BLOOD_PRESSURE_CODING), () => '   /    mmHg')
    .with(codeFromCodeableConcept(HEART_RATE_CODING), () => 'bpm  ')
    .with(codeFromCodeableConcept(RESPIRATORY_RATE_CODING), () => '/min ')
    .with(codeFromCodeableConcept(OXYGEN_SATURATION_CODING), () => '%    ')
    .with(codeFromCodeableConcept(PAIN_SEVERITY_CODING), () => '0–10 ')
    .otherwise(() => '     ')
}
